import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { useFormPersistence } from '@/hooks/useFormPersistence';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Progress } from '@/components/ui/progress';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  ChevronRight, ChevronLeft, User, Target,
  Calendar, CheckCircle2, Loader2, Video, Clock,
  Dumbbell, Factory, Piano, Mic, Sparkles, GraduationCap, Gift,
  Heart, Lightbulb, Music, RefreshCw, Download, Search, Home
} from 'lucide-react';
import { pdf } from '@react-pdf/renderer';
import StudentWelcomePDF from '@/pdf/StudentWelcomePDF';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

const classroomIcons = {
  'music-gym': Dumbbell,
  'music-factory': Factory,
  'modular-piano': Piano,
  'theory-ear-training': Mic,
  'seasonal-genre': Calendar,
  'new-offerings': Sparkles,
  'foundation-lessons': GraduationCap,
  'perks-tiers': Gift,
};

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const TIMES = ['Morning (6am-12pm)', 'Afternoon (12pm-5pm)', 'Evening (5pm-9pm)', 'Night (9pm-12am)'];

const STEPS = [
  { id: 1, title: 'Welcome Back', icon: User },
  { id: 2, title: 'Your Experience', icon: Heart },
  { id: 3, title: 'New Classes', icon: CheckCircle2 },
  { id: 4, title: 'Next Goals', icon: Target },
  { id: 5, title: 'Schedule', icon: Calendar },
];

export const ReturningStudentForm = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [classrooms, setClassrooms] = useState([]);
  const [hourPackages, setHourPackages] = useState([]);
  const [expandedClassroom, setExpandedClassroom] = useState(null);
  
  const [formData, setFormData, clearSavedData] = useFormPersistence('returning_student', {
    full_name: '',
    email: '',
    phone_number: '',
    semesters_completed: '',
    last_package_hours: '',
    favorite_classes: '',
    what_worked_well: '',
    what_to_improve: '',
    class_interests: [],
    goals: '',
    songs_to_learn: '',
    new_areas_to_explore: '',
    preferred_days: [],
    preferred_times: [],
    interested_in_person: false,
  });

  useEffect(() => {
    fetchClassrooms();
    fetchHourPackages();
  }, []);

  const fetchClassrooms = async () => {
    try {
      const response = await axios.get(`${API}/classrooms`);
      setClassrooms(response.data);
    } catch (error) {
      console.error('Error fetching classrooms:', error);
      toast.error('Failed to load class options');
    }
  };

  const fetchHourPackages = async () => {
    try {
      const response = await axios.get(`${API}/hour-packages`);
      setHourPackages(response.data);
    } catch (error) {
      console.error('Error fetching hour packages:', error);
    }
  };

  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleArrayItem = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter(item => item !== value)
        : [...prev[field], value]
    }));
  };

  const toggleClassInterest = (classData, classroomName) => {
    const existingIndex = formData.class_interests.findIndex(
      ci => ci.class_id === classData.id
    );
    
    if (existingIndex >= 0) {
      setFormData(prev => ({
        ...prev,
        class_interests: prev.class_interests.filter(ci => ci.class_id !== classData.id)
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        class_interests: [...prev.class_interests, {
          class_id: classData.id,
          class_name: classData.name,
          classroom_name: classroomName,
          prefer_recording: false
        }]
      }));
    }
  };

  const toggleRecordingPreference = (classId) => {
    setFormData(prev => ({
      ...prev,
      class_interests: prev.class_interests.map(ci =>
        ci.class_id === classId ? { ...ci, prefer_recording: !ci.prefer_recording } : ci
      )
    }));
  };

  const validateStep = (step) => {
    switch (step) {
      case 1:
        return formData.full_name && formData.email && formData.phone_number && 
               formData.semesters_completed && formData.last_package_hours;
      case 2:
        return formData.favorite_classes && formData.what_worked_well;
      case 3:
        return formData.class_interests.length > 0;
      case 4:
        return formData.goals;
      case 5:
        return formData.preferred_days.length > 0 && formData.preferred_times.length > 0;
      default:
        return true;
    }
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 5));
    } else {
      toast.error('Please fill in all required fields');
    }
  };

  const handlePrevious = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    if (!validateStep(5)) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);
    try {
      const submitData = {
        ...formData,
        semesters_completed: parseInt(formData.semesters_completed),
        last_package_hours: parseInt(formData.last_package_hours)
      };
      await axios.post(`${API}/students/returning`, submitData);
      clearSavedData();
      setSubmitted(true);
      toast.success('Welcome back! Submission received.');
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Failed to submit. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const progress = (currentStep / 5) * 100;

  const handleDownloadPDF = async () => {
    setIsGeneratingPDF(true);
    try {
      const blob = await pdf(
        <StudentWelcomePDF formData={formData} studentType="returning" classrooms={classrooms} />
      ).toBlob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `NSM_Course_Selection_${formData.full_name.replace(/\s+/g, '_')}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Failed to generate PDF. Please try again.');
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#050505]">
        <Header />
        <main className="pt-24 pb-16">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="glass-card rounded-2xl p-10">
              <div className="w-20 h-20 rounded-full bg-purple-500/20 flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="text-purple-400" size={40} />
              </div>
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-[#EDEDED] mb-4">
                Welcome Back!
              </h1>
              <p className="text-[#A1A1AA] text-lg mb-8">
                Great to have you back, {formData.full_name.split(' ')[0]}! We'll review your selections and reach out within 48 hours with a renewed course plan.
              </p>

              <div className="glass-card rounded-xl p-6 mb-8 border border-[#D4AF37]/30">
                <h3 className="font-heading text-xl text-[#F5F5DC] mb-3">Download Your Course Selection Guide</h3>
                <p className="text-[#A1A1AA] text-sm mb-6">
                  A personalized PDF summarizing your class selections, past experience, and goals for the new semester.
                </p>
                <Button
                  onClick={handleDownloadPDF}
                  disabled={isGeneratingPDF}
                  className="btn-gold rounded-full px-8 py-4 text-lg"
                >
                  {isGeneratingPDF ? (
                    <>
                      <Loader2 className="mr-2 animate-spin" size={20} />
                      Generating PDF...
                    </>
                  ) : (
                    <>
                      <Download size={20} className="mr-2" />
                      Download PDF Guide
                    </>
                  )}
                </Button>
              </div>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <a href="/track">
                  <Button variant="outline" className="rounded-full px-6 border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black">
                    <Search size={16} className="mr-2" />
                    Track Your Application
                  </Button>
                </a>
                <a href="/">
                  <Button variant="outline" className="rounded-full px-6 border-white/20 text-[#EDEDED] hover:bg-white/10">
                    <Home size={16} className="mr-2" />
                    Back to Home
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]">
      <Header />

      <main className="pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-12">
            <p className="font-accent text-[#D4AF37] text-sm tracking-[0.2em] uppercase mb-4">
              Welcome Back
            </p>
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-[#EDEDED] mb-4">
              Ready for Another Semester?
            </h1>
            <p className="text-[#A1A1AA] max-w-2xl mx-auto">
              Great to have you back! A lot has changed since you were last here. 
              We've added new classes, jam sessions, and in-person workshops. 
              Let's build your next semester together.
            </p>
          </div>

          {/* What's New Banner */}
          <div className="glass-card rounded-2xl p-6 mb-8 border-[#D4AF37]/30">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-[#D4AF37]/20 flex items-center justify-center flex-shrink-0">
                <RefreshCw className="text-[#D4AF37]" size={24} />
              </div>
              <div>
                <h3 className="font-heading text-xl text-[#F5F5DC] mb-2">Since You've Been Gone...</h3>
                <ul className="space-y-2 text-[#A1A1AA]">
                  <li className="flex items-center gap-2">
                    <Sparkles size={14} className="text-[#D4AF37]" />
                    <span>New jam sessions where you play with other musicians</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Sparkles size={14} className="text-[#D4AF37]" />
                    <span>In-person workshops at our Bangalore studio</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Sparkles size={14} className="text-[#D4AF37]" />
                    <span>HD recordings for every class—learn on your schedule</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Sparkles size={14} className="text-[#D4AF37]" />
                    <span>More genres: Coke Studio, Regional music, Latin, and more</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="mb-12">
            <div className="flex justify-between mb-4">
              {STEPS.map((step) => (
                <div 
                  key={step.id}
                  className={`flex flex-col items-center ${
                    step.id <= currentStep ? 'text-[#D4AF37]' : 'text-[#A1A1AA]'
                  }`}
                >
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-2 ${
                    step.id < currentStep 
                      ? 'bg-[#D4AF37] text-black'
                      : step.id === currentStep 
                        ? 'border-2 border-[#D4AF37] text-[#D4AF37]'
                        : 'border border-[#2A2A2A] text-[#A1A1AA]'
                  }`}>
                    {step.id < currentStep ? (
                      <CheckCircle2 size={20} />
                    ) : (
                      <step.icon size={18} />
                    )}
                  </div>
                  <span className="text-xs hidden md:block">{step.title}</span>
                </div>
              ))}
            </div>
            <Progress value={progress} className="h-2 bg-[#2A2A2A]" />
          </div>

          {/* Form Container */}
          <div className="glass-card rounded-2xl p-8">
            {/* Step 1: Basic Info */}
            {currentStep === 1 && (
              <div className="space-y-6 animate-fade-in-up">
                <h2 className="font-heading text-2xl text-[#F5F5DC] mb-6">Let's catch up</h2>
                
                <div className="space-y-2">
                  <Label htmlFor="full_name" className="text-[#F5F5DC]">Full Name *</Label>
                  <Input
                    id="full_name"
                    value={formData.full_name}
                    onChange={(e) => updateFormData('full_name', e.target.value)}
                    className="bg-black/50 border-white/10 text-[#EDEDED]"
                    placeholder="Your full name"
                    data-testid="input-full-name"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-[#F5F5DC]">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => updateFormData('email', e.target.value)}
                    className="bg-black/50 border-white/10 text-[#EDEDED]"
                    placeholder="your@email.com"
                    data-testid="input-email"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-[#F5F5DC]">Phone Number *</Label>
                  <Input
                    id="phone"
                    value={formData.phone_number}
                    onChange={(e) => updateFormData('phone_number', e.target.value)}
                    className="bg-black/50 border-white/10 text-[#EDEDED]"
                    placeholder="Your phone number"
                    data-testid="input-phone"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-[#F5F5DC]">Semesters Completed *</Label>
                    <Select 
                      value={formData.semesters_completed}
                      onValueChange={(value) => updateFormData('semesters_completed', value)}
                    >
                      <SelectTrigger className="bg-black/50 border-white/10 text-[#EDEDED]" data-testid="select-semesters">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#121212] border-[#2A2A2A]">
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => (
                          <SelectItem key={num} value={String(num)} className="text-[#EDEDED]">
                            {num} {num === 1 ? 'semester' : 'semesters'}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-[#F5F5DC]">Last Package (Hours) *</Label>
                    <Select 
                      value={formData.last_package_hours}
                      onValueChange={(value) => updateFormData('last_package_hours', value)}
                    >
                      <SelectTrigger className="bg-black/50 border-white/10 text-[#EDEDED]" data-testid="select-package">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#121212] border-[#2A2A2A]">
                        {hourPackages.map(pkg => (
                          <SelectItem key={pkg.id} value={String(pkg.hours)} className="text-[#EDEDED]">
                            {pkg.hours} Hours
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Your Experience */}
            {currentStep === 2 && (
              <div className="space-y-6 animate-fade-in-up">
                <h2 className="font-heading text-2xl text-[#F5F5DC] mb-6">Tell us about your experience</h2>
                <p className="text-[#A1A1AA] mb-6">
                  This helps us understand what worked for you and how we can make your next semester even better.
                </p>
                
                <div className="space-y-2">
                  <Label htmlFor="favorite_classes" className="text-[#F5F5DC]">
                    What were your favorite classes or topics? *
                  </Label>
                  <Textarea
                    id="favorite_classes"
                    value={formData.favorite_classes}
                    onChange={(e) => updateFormData('favorite_classes', e.target.value)}
                    className="bg-black/50 border-white/10 text-[#EDEDED] min-h-[100px]"
                    placeholder="e.g., I loved the improvisation sessions, the Coke Studio arrangements, and the ear training exercises..."
                    data-testid="textarea-favorites"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="what_worked_well" className="text-[#F5F5DC]">
                    What worked well for you? *
                  </Label>
                  <Textarea
                    id="what_worked_well"
                    value={formData.what_worked_well}
                    onChange={(e) => updateFormData('what_worked_well', e.target.value)}
                    className="bg-black/50 border-white/10 text-[#EDEDED] min-h-[100px]"
                    placeholder="e.g., The one-on-one attention, the flexibility of scheduling, being able to learn songs I actually wanted to play..."
                    data-testid="textarea-worked-well"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="what_to_improve" className="text-[#F5F5DC]">
                    Anything we could do better?
                  </Label>
                  <Textarea
                    id="what_to_improve"
                    value={formData.what_to_improve}
                    onChange={(e) => updateFormData('what_to_improve', e.target.value)}
                    className="bg-black/50 border-white/10 text-[#EDEDED] min-h-[100px]"
                    placeholder="Your honest feedback helps us improve..."
                    data-testid="textarea-improve"
                  />
                </div>
              </div>
            )}

            {/* Step 3: Class Selection */}
            {currentStep === 3 && (
              <div className="space-y-6 animate-fade-in-up">
                <div>
                  <h2 className="font-heading text-2xl text-[#F5F5DC] mb-2">What's next for you?</h2>
                  <p className="text-[#A1A1AA] text-sm mb-2">
                    Pick the classes you want to explore this semester. We've added a lot of new stuff!
                  </p>
                  <div className="flex items-center gap-2 text-sm text-[#D4AF37] bg-[#D4AF37]/10 p-3 rounded-lg">
                    <Video size={16} />
                    <span>Can't attend live? Mark classes for HD Recording</span>
                  </div>
                </div>

                <div className="space-y-4">
                  {classrooms.map(classroom => {
                    const IconComponent = classroomIcons[classroom.id] || Music;
                    const isExpanded = expandedClassroom === classroom.id;
                    
                    return (
                      <div key={classroom.id} className="border border-white/10 rounded-xl overflow-hidden">
                        <button
                          onClick={() => setExpandedClassroom(isExpanded ? null : classroom.id)}
                          className="w-full flex items-center justify-between p-4 hover:bg-white/5 transition-colors"
                          data-testid={`classroom-toggle-${classroom.id}`}
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-[#D4AF37]/10 flex items-center justify-center">
                              <IconComponent className="text-[#D4AF37]" size={20} />
                            </div>
                            <div className="text-left">
                              <h3 className="font-heading text-[#F5F5DC]">{classroom.name}</h3>
                              <p className="text-[#A1A1AA] text-xs">{classroom.classes?.length || 0} classes</p>
                            </div>
                          </div>
                          <ChevronRight className={`text-[#A1A1AA] transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                        </button>
                        
                        {isExpanded && (
                          <div className="border-t border-white/10 p-4 space-y-3 bg-black/20">
                            {classroom.classes?.map(cls => {
                              const isSelected = formData.class_interests.some(ci => ci.class_id === cls.id);
                              const classInterest = formData.class_interests.find(ci => ci.class_id === cls.id);
                              
                              return (
                                <div
                                  key={cls.id}
                                  className={`p-4 rounded-lg border transition-all ${
                                    isSelected
                                      ? 'border-[#D4AF37] bg-[#D4AF37]/10'
                                      : 'border-white/10 hover:border-[#D4AF37]/30'
                                  }`}
                                >
                                  <div 
                                    className="flex items-start gap-3 cursor-pointer"
                                    onClick={() => toggleClassInterest(cls, classroom.name)}
                                  >
                                    <Checkbox 
                                      checked={isSelected}
                                      className="mt-1"
                                      data-testid={`checkbox-class-${cls.id}`}
                                    />
                                    <div className="flex-1">
                                      <h4 className="text-[#F5F5DC] font-medium">{cls.name}</h4>
                                      <p className="text-[#A1A1AA] text-sm mt-1">{cls.description}</p>
                                      {cls.duration_minutes && (
                                        <div className="flex items-center gap-1 mt-2 text-xs text-[#A1A1AA]">
                                          <Clock size={12} />
                                          <span>{cls.duration_minutes} minutes</span>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                  
                                  {isSelected && (
                                    <div className="mt-4 pt-4 border-t border-white/10">
                                      <div 
                                        className="flex items-center gap-3 cursor-pointer"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          toggleRecordingPreference(cls.id);
                                        }}
                                      >
                                        <Checkbox 
                                          checked={classInterest?.prefer_recording || false}
                                          data-testid={`checkbox-recording-${cls.id}`}
                                        />
                                        <div className="flex items-center gap-2">
                                          <Video size={14} className="text-[#D4AF37]" />
                                          <span className="text-sm text-[#A1A1AA]">
                                            Rely on HD Recording (Can't attend live)
                                          </span>
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {formData.class_interests.length > 0 && (
                  <div className="bg-[#121212] rounded-lg p-4">
                    <h4 className="text-[#D4AF37] font-medium mb-2">
                      Selected ({formData.class_interests.length})
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {formData.class_interests.map(ci => (
                        <span 
                          key={ci.class_id}
                          className="px-3 py-1 rounded-full bg-[#D4AF37]/20 text-[#D4AF37] text-sm"
                        >
                          {ci.class_name}
                          {ci.prefer_recording && ' (Recording)'}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Step 4: Goals */}
            {currentStep === 4 && (
              <div className="space-y-6 animate-fade-in-up">
                <h2 className="font-heading text-2xl text-[#F5F5DC] mb-6">Goals for this semester</h2>
                
                <div className="space-y-2">
                  <Label htmlFor="goals" className="text-[#F5F5DC]">What do you want to achieve? *</Label>
                  <Textarea
                    id="goals"
                    value={formData.goals}
                    onChange={(e) => updateFormData('goals', e.target.value)}
                    className="bg-black/50 border-white/10 text-[#EDEDED] min-h-[120px]"
                    placeholder="e.g., I want to be able to jam confidently with other musicians, learn to play by ear better..."
                    data-testid="textarea-goals"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="songs" className="text-[#F5F5DC]">Songs you want to learn</Label>
                  <Textarea
                    id="songs"
                    value={formData.songs_to_learn}
                    onChange={(e) => updateFormData('songs_to_learn', e.target.value)}
                    className="bg-black/50 border-white/10 text-[#EDEDED] min-h-[100px]"
                    placeholder="Any specific songs or artists?"
                    data-testid="textarea-songs"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="new_areas" className="text-[#F5F5DC]">New areas you want to explore</Label>
                  <Textarea
                    id="new_areas"
                    value={formData.new_areas_to_explore}
                    onChange={(e) => updateFormData('new_areas_to_explore', e.target.value)}
                    className="bg-black/50 border-white/10 text-[#EDEDED] min-h-[100px]"
                    placeholder="e.g., Jazz improvisation, production, Latin rhythms, playing by ear..."
                    data-testid="textarea-new-areas"
                  />
                </div>
              </div>
            )}

            {/* Step 5: Schedule */}
            {currentStep === 5 && (
              <div className="space-y-8 animate-fade-in-up">
                <h2 className="font-heading text-2xl text-[#F5F5DC] mb-6">When works for you?</h2>
                
                <div className="space-y-4">
                  <Label className="text-[#F5F5DC]">Preferred Days *</Label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {DAYS.map(day => (
                      <div
                        key={day}
                        onClick={() => toggleArrayItem('preferred_days', day)}
                        className={`p-3 rounded-lg border text-center cursor-pointer transition-all ${
                          formData.preferred_days.includes(day)
                            ? 'border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]'
                            : 'border-white/10 text-[#A1A1AA] hover:border-[#D4AF37]/50'
                        }`}
                        data-testid={`day-${day.toLowerCase()}`}
                      >
                        {day}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="space-y-4">
                  <Label className="text-[#F5F5DC]">Preferred Times *</Label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {TIMES.map(time => (
                      <div
                        key={time}
                        onClick={() => toggleArrayItem('preferred_times', time)}
                        className={`p-3 rounded-lg border text-center cursor-pointer transition-all ${
                          formData.preferred_times.includes(time)
                            ? 'border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]'
                            : 'border-white/10 text-[#A1A1AA] hover:border-[#D4AF37]/50'
                        }`}
                        data-testid={`time-${time.split(' ')[0].toLowerCase()}`}
                      >
                        {time}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="space-y-4">
                  <div 
                    className={`flex items-center gap-4 p-4 rounded-lg border cursor-pointer transition-all ${
                      formData.interested_in_person
                        ? 'border-[#D4AF37] bg-[#D4AF37]/10'
                        : 'border-white/10 hover:border-[#D4AF37]/50'
                    }`}
                    onClick={() => updateFormData('interested_in_person', !formData.interested_in_person)}
                  >
                    <Checkbox 
                      checked={formData.interested_in_person}
                      data-testid="checkbox-in-person"
                    />
                    <div>
                      <Label className="text-[#F5F5DC] cursor-pointer">
                        I'm interested in in-person sessions at the Bangalore studio
                      </Label>
                      <p className="text-[#A1A1AA] text-sm">
                        Jam sessions, workshops, and studio recording
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex justify-between mt-8 pt-6 border-t border-white/10">
              {currentStep > 1 ? (
                <Button
                  variant="outline"
                  onClick={handlePrevious}
                  className="border-white/20 text-[#EDEDED] hover:bg-white/5"
                  data-testid="btn-previous"
                >
                  <ChevronLeft className="mr-2" size={18} />
                  Previous
                </Button>
              ) : (
                <div />
              )}
              
              {currentStep < 5 ? (
                <Button
                  onClick={handleNext}
                  className="btn-gold rounded-full px-8"
                  data-testid="btn-next"
                >
                  Next Step
                  <ChevronRight className="ml-2" size={18} />
                </Button>
              ) : (
                <Button
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className="btn-gold rounded-full px-8"
                  data-testid="btn-submit"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 animate-spin" size={18} />
                      Submitting...
                    </>
                  ) : (
                    <>
                      Submit & Renew
                      <CheckCircle2 className="ml-2" size={18} />
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ReturningStudentForm;
